'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/context/LanguageContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { apiClient } from '@/lib/api-client'
import { toast } from '@/components/ui/toast'
import { useSession } from 'next-auth/react'
import {
  formatRecipeForLang,
  type LocalizedRecipe,
  type TrilingualRecipeRaw,
} from '@/lib/recipe-format'

type Recipe = LocalizedRecipe

export default function HomePage() {
  const { t, language } = useLanguage()
  const { status } = useSession()
  const isAuthenticated = status === 'authenticated'

  const [ingredients, setIngredients] = useState<string[]>([])
  const [textInput, setTextInput] = useState('')
  const [isListening, setIsListening] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [expandedRecipeId, setExpandedRecipeId] = useState<string | null>(null)
  const [speakingId, setSpeakingId] = useState<string | null>(null)
  const [savedRecipes, setSavedRecipes] = useState<Recipe[]>([])
  const recipesRawRef = useRef<Record<string, TrilingualRecipeRaw>>({})
  const synthRef = useRef<SpeechSynthesis | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      synthRef.current = window.speechSynthesis
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      const stored = localStorage.getItem('swaastha_saved_recipes')
      if (stored) {
        try {
          setSavedRecipes(JSON.parse(stored))
        } catch (e) {
          console.error(e)
        }
      }
      return
    }

    const fetchSaved = async () => {
      try {
        const data = await apiClient.get<
          Array<{ recipe_id: string; recipe_data: Recipe }>
        >(`/api/ai/recipes/saved?lang=${language}`)
        setSavedRecipes(data.map((r) => r.recipe_data))
      } catch (e) {
        console.error(e)
      }
    }
    fetchSaved()
  }, [isAuthenticated, language])

  useEffect(() => {
    const rawRecipes = Object.values(recipesRawRef.current)
    if (rawRecipes.length > 0) {
      setRecipes(rawRecipes.map((r) => formatRecipeForLang(r, language)))
    }
  }, [language])

  useEffect(() => {
    return () => {
      if (synthRef.current) synthRef.current.cancel()
    }
  }, [])

  const handleAddIngredient = (text: string) => {
    const cleaned = text.trim().toLowerCase()
    if (!cleaned) return
    if (ingredients.includes(cleaned)) {
      toast.warning('Ingredient already added.')
      return
    }
    setIngredients([...ingredients, cleaned])
    setTextInput('')
  }

  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index))
  }

  const handleVoiceInput = () => {
    const win = window as typeof window & {
      SpeechRecognition?: new () => {
        continuous: boolean
        lang: string
        interimResults: boolean
        onstart: (() => void) | null
        onresult: ((event: { results: { 0: { 0: { transcript: string } } } }) => void) | null
        onerror: (() => void) | null
        onend: (() => void) | null
        start: () => void
      }
      webkitSpeechRecognition?: new () => {
        continuous: boolean
        lang: string
        interimResults: boolean
        onstart: (() => void) | null
        onresult: ((event: { results: { 0: { 0: { transcript: string } } } }) => void) | null
        onerror: (() => void) | null
        onend: (() => void) | null
        start: () => void
      }
    }

    const SpeechRecognition = win.SpeechRecognition || win.webkitSpeechRecognition

    if (!SpeechRecognition) {
      toast.error('Voice input is not supported in this browser. Please use Chrome, Safari, or Edge.')
      return
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.lang = language === 'en' ? 'en-IN' : language === 'hi' ? 'hi-IN' : 'mr-IN'
    recognition.interimResults = false

    recognition.onstart = () => setIsListening(true)

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript
      handleAddIngredient(transcript)
      toast.success(`Added: ${transcript}`)
    }

    recognition.onerror = () => {
      toast.error('Speech recognition failed. Try again.')
      setIsListening(false)
    }

    recognition.onend = () => setIsListening(false)

    recognition.start()
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]
    setIsUploading(true)
    toast.info(t('input.image.analyzing'))

    const formData = new FormData()
    formData.append('file', file)

    try {
      const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'

      const response = await fetch(`${apiUrl}${basePath}/api/ai/recipes/extract-ingredients`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) throw new Error('Failed to analyze image')

      const data = await response.json()
      const newItems: string[] = data.ingredients || []

      if (newItems.length > 0) {
        setIngredients(Array.from(new Set([...ingredients, ...newItems])))
        toast.success(`Detected ${newItems.length} ingredients!`)
      } else {
        toast.warning('No ingredients could be detected in the image.')
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to extract ingredients from image.')
    } finally {
      setIsUploading(false)
      e.target.value = ''
    }
  }

  const handleGenerateRecipes = async () => {
    if (ingredients.length === 0) return

    setIsGenerating(true)
    setExpandedRecipeId(null)
    if (synthRef.current) synthRef.current.cancel()
    setSpeakingId(null)

    try {
      const data = await apiClient.post<{
        recipes: Recipe[]
        recipes_raw?: TrilingualRecipeRaw[]
      }>('/api/ai/recipes/generate', {
        ingredients,
        lang: language,
      })

      if (data.recipes_raw?.length) {
        recipesRawRef.current = Object.fromEntries(
          data.recipes_raw.map((r) => [r.id, r])
        )
      }

      setRecipes(data.recipes || [])
      toast.success('Recipes generated successfully!')
    } catch (err) {
      console.error(err)
      toast.error('Failed to generate recipes. Using offline fallbacks.')
    } finally {
      setIsGenerating(false)
    }
  }

  const toggleReadAloud = (recipe: Recipe) => {
    if (!synthRef.current) return

    if (speakingId === recipe.id) {
      synthRef.current.cancel()
      setSpeakingId(null)
      return
    }

    synthRef.current.cancel()
    setSpeakingId(recipe.id)

    const script = `${recipe.name}. ${recipe.description}. Difficulty is ${recipe.difficulty}. Preparation time ${recipe.prep_time_mins} minutes, cooking time ${recipe.cook_time_mins} minutes. Ingredients needed: ${recipe.ingredients.map((i) => `${i.quantity} of ${i.name}`).join(', ')}. Instructions: ${recipe.instructions.join('. ')}`

    const utterance = new SpeechSynthesisUtterance(script)
    utterance.lang = language === 'mr' ? 'mr-IN' : language === 'hi' ? 'hi-IN' : 'en-IN'
    utterance.onend = () => setSpeakingId(null)
    utterance.onerror = () => setSpeakingId(null)

    synthRef.current.speak(utterance)
  }

  const toggleSaveRecipe = async (recipe: Recipe) => {
    const exists = savedRecipes.some((r) => r.id === recipe.id)

    if (isAuthenticated) {
      try {
        if (exists) {
          await apiClient.delete(`/api/ai/recipes/saved/${recipe.id}`)
          setSavedRecipes((prev) => prev.filter((r) => r.id !== recipe.id))
          toast.success('Recipe unsaved.')
        } else {
          const rawPayload = recipesRawRef.current[recipe.id]
          await apiClient.post(
            `/api/ai/recipes/saved?lang=${language}`,
            {
              recipe_id: recipe.id,
              recipe_data: rawPayload ?? recipe,
            }
          )
          setSavedRecipes((prev) => [...prev, recipe])
          toast.success('Recipe saved to your profile!')
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        toast.error(`Failed to update saved recipe. ${message}`)
      }
      return
    }

    let nextSaved = [...savedRecipes]
    if (exists) {
      nextSaved = nextSaved.filter((r) => r.id !== recipe.id)
      toast.success('Recipe unsaved.')
    } else {
      nextSaved.push(recipe)
      toast.success('Recipe saved to this device!')
    }

    setSavedRecipes(nextSaved)
    localStorage.setItem('swaastha_saved_recipes', JSON.stringify(nextSaved))
  }

  const difficultyLabel = (difficulty: Recipe['difficulty']) => {
    if (difficulty === 'Easy') return t('recipe.easy')
    if (difficulty === 'Medium') return t('recipe.medium')
    return t('recipe.hard')
  }

  return (
    <div className='space-y-8 pb-16'>
      <div className='py-4 text-center'>
        <span className='inline-block rounded-full bg-brand-cornflower/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-navy'>
          {t('hero.tagline')}
        </span>
        <h1 className='text-display-3 mt-3 font-bold text-brand-navy lg:text-display-2'>
          {t('hero.headline_part1')} <br />
          <span className='text-gradient'>{t('hero.headline_part2')}</span>
        </h1>
        <p className='mx-auto mt-4 max-w-lg text-base text-muted-foreground'>
          {t('hero.subheadline')}
        </p>
      </div>

      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={2} scale={1.1} />
        <CardHeader className='pb-3'>
          <CardTitle className='text-lg font-semibold'>{t('input.title')}</CardTitle>
          <CardDescription>{t('input.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex gap-2'>
            <input
              type='text'
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddIngredient(textInput)}
              placeholder={t('input.placeholder')}
              aria-label={t('input.placeholder')}
              className={cn(
                'flex-1 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none',
                'transition-all focus:border-brand-cornflower/50 focus:ring-2 focus:ring-brand-cornflower/20'
              )}
            />
            <Button onClick={() => handleAddIngredient(textInput)} variant='default'>
              {t('input.add')}
            </Button>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleVoiceInput}
              disabled={isListening}
              className={cn(
                'gap-2 rounded-full border-dashed',
                isListening && 'border-brand-cornflower bg-brand-cornflower/5 text-brand-navy'
              )}
            >
              <Icons.mic
                className={cn('h-4 w-4 text-brand-cornflower', isListening && 'animate-pulse')}
                strokeWidth={1.5}
              />
              {isListening ? t('input.voice.listening') : t('input.voice')}
            </Button>

            <label className='inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-dashed border-gray-200 bg-white px-4 text-xs font-semibold transition-all hover:border-brand-cornflower/30 hover:bg-gray-50'>
              <Icons.camera className='h-4 w-4 text-brand-cornflower' strokeWidth={1.5} />
              <span>{isUploading ? t('input.image.analyzing') : t('input.image')}</span>
              <input
                type='file'
                accept='image/*'
                onChange={handleImageUpload}
                disabled={isUploading}
                className='hidden'
                aria-label={t('input.image')}
              />
            </label>

            {ingredients.length > 0 && (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setIngredients([])}
                className='ml-auto text-red-500 hover:text-red-600'
              >
                <Icons.trash className='mr-1 h-4 w-4' strokeWidth={1.5} />
                {t('input.clear')}
              </Button>
            )}
          </div>

          <AnimatePresence>
            {ingredients.length > 0 && (
              <div className='flex flex-wrap gap-2 border-t pt-2'>
                {ingredients.map((ing, idx) => (
                  <motion.span
                    key={`${ing}-${idx}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className='inline-flex items-center gap-2 rounded-full border border-brand-navy/10 bg-brand-navy/5 px-3.5 py-1.5 text-sm font-semibold text-brand-navy'
                  >
                    <span>{ing}</span>
                    <button
                      type='button'
                      onClick={() => handleRemoveIngredient(idx)}
                      className='hover:text-red-500'
                      aria-label={`Remove ${ing}`}
                    >
                      <Icons.close className='h-3 w-3' strokeWidth={1.5} />
                    </button>
                  </motion.span>
                ))}
              </div>
            )}
          </AnimatePresence>

          <div className='pt-2'>
            <Button
              onClick={handleGenerateRecipes}
              disabled={ingredients.length === 0 || isGenerating}
              variant='gradient'
              size='lg'
              className='w-full'
            >
              {isGenerating ? (
                <>
                  <Icons.loader className='mr-2 h-5 w-5 animate-spin' strokeWidth={1.5} />
                  {t('input.generating')}
                </>
              ) : (
                <>
                  <Icons.sparkles className='mr-2 h-5 w-5' strokeWidth={1.5} />
                  {t('input.generate')}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className='space-y-4'>
        {recipes.length === 0 && (
          <p className='text-center text-sm text-muted-foreground'>{t('recipe.no_recipes')}</p>
        )}

        {recipes.length > 0 && (
          <div className='grid gap-6 md:grid-cols-3'>
            {recipes.map((recipe) => {
              const isExpanded = expandedRecipeId === recipe.id
              const isSaved = savedRecipes.some((r) => r.id === recipe.id)
              const isSpeaking = speakingId === recipe.id

              return (
                <Card key={recipe.id} className='relative flex h-full flex-col overflow-hidden bg-white'>
                  <CardWatermark opacity={2} scale={0.8} />
                  <CardContent className='relative z-10 flex h-full flex-col space-y-4 p-6'>
                    <div className='flex items-start justify-between gap-3'>
                      <div>
                        <h3 className='text-base font-bold leading-snug text-brand-navy'>
                          {recipe.name}
                        </h3>
                        <span
                          className={cn(
                            'mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                            recipe.difficulty === 'Easy' && 'bg-emerald-100 text-emerald-700',
                            recipe.difficulty === 'Medium' && 'bg-amber-100 text-amber-700',
                            recipe.difficulty === 'Hard' && 'bg-red-100 text-red-700'
                          )}
                        >
                          {difficultyLabel(recipe.difficulty)}
                        </span>
                      </div>

                      <div className='flex items-center gap-1'>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          onClick={() => toggleReadAloud(recipe)}
                          aria-label={isSpeaking ? t('recipe.stop_aloud') : t('recipe.read_aloud')}
                          className={
                            isSpeaking ? 'bg-brand-cornflower/10 text-brand-cornflower' : 'text-brand-muted'
                          }
                        >
                          {isSpeaking ? (
                            <Icons.logout className='h-4 w-4 animate-pulse' strokeWidth={1.5} />
                          ) : (
                            <Icons.bell className='h-4 w-4' strokeWidth={1.5} />
                          )}
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          onClick={() => toggleSaveRecipe(recipe)}
                          aria-label={isSaved ? t('recipe.saved') : t('recipe.save')}
                          className={isSaved ? 'bg-emerald-50 text-emerald-600' : 'text-brand-muted'}
                        >
                          <Icons.checkCircle className='h-4 w-4' strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>

                    <p className='line-clamp-3 text-sm leading-relaxed text-muted-foreground'>
                      {recipe.description}
                    </p>

                    <div className='grid grid-cols-3 gap-2 border-y border-gray-100 py-2 text-center text-xs'>
                      <div>
                        <p className='text-[10px] uppercase text-brand-muted'>{t('recipe.prep')}</p>
                        <p className='mt-0.5 font-bold text-brand-navy'>
                          {recipe.prep_time_mins} {t('recipe.mins')}
                        </p>
                      </div>
                      <div>
                        <p className='text-[10px] uppercase text-brand-muted'>{t('recipe.cook')}</p>
                        <p className='mt-0.5 font-bold text-brand-navy'>
                          {recipe.cook_time_mins} {t('recipe.mins')}
                        </p>
                      </div>
                      <div>
                        <p className='text-[10px] uppercase text-brand-muted'>{t('recipe.servings')}</p>
                        <p className='mt-0.5 font-bold text-brand-navy'>{recipe.serving_size}</p>
                      </div>
                    </div>

                    <div className='flex flex-wrap gap-1.5'>
                      {recipe.nutrition_highlights.map((h, i) => (
                        <span
                          key={i}
                          className='inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700'
                        >
                          {h}
                        </span>
                      ))}
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className='space-y-3 overflow-hidden border-t pt-3 text-sm leading-relaxed'
                        >
                          <div>
                            <p className='mb-1.5 font-bold text-brand-navy'>Ingredients Required:</p>
                            <ul className='list-disc space-y-1 pl-4 text-xs text-muted-foreground'>
                              {recipe.ingredients.map((ing, i) => (
                                <li key={i}>
                                  {ing.name} ({ing.quantity})
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <p className='mb-1.5 font-bold text-brand-navy'>Instructions:</p>
                            <ol className='list-decimal space-y-2 pl-4 text-xs text-muted-foreground'>
                              {recipe.instructions.map((step, i) => (
                                <li key={i} className='pl-1'>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>

                          <Button
                            variant='outline'
                            size='sm'
                            className='mt-2 w-full gap-2 border-red-200 text-red-600 hover:bg-red-50'
                            onClick={() => window.open(recipe.youtube_search_url, '_blank')}
                          >
                            <Icons.globe className='h-4 w-4' strokeWidth={1.5} />
                            {t('recipe.youtube')}
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <Button
                      variant='ghost'
                      onClick={() => setExpandedRecipeId(isExpanded ? null : recipe.id)}
                      className='mt-auto w-full justify-between text-brand-navy'
                    >
                      <span>{isExpanded ? t('recipe.hide_full') : t('recipe.show_full')}</span>
                      {isExpanded ? (
                        <Icons.chevronUp className='h-4 w-4' strokeWidth={1.5} />
                      ) : (
                        <Icons.chevronDown className='h-4 w-4' strokeWidth={1.5} />
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {savedRecipes.length > 0 && (
        <div className='space-y-4 border-t border-dashed pt-6'>
          <div>
            <h2 className='flex items-center gap-2 text-lg font-bold text-brand-navy'>
              <Icons.checkCircle className='h-5 w-5 text-emerald-500' strokeWidth={1.5} />
              Saved Recipes
            </h2>
            <p className='text-xs text-brand-muted'>
              Bookmarked on this browser session for easy offline viewing.
            </p>
          </div>

          <div className='grid gap-4 md:grid-cols-4'>
            {savedRecipes.map((recipe) => (
              <Card
                key={`saved-${recipe.id}`}
                className='border border-gray-100 bg-white/50 p-4 hover:shadow-glass'
              >
                <div className='flex justify-between gap-2'>
                  <h4 className='truncate text-sm font-semibold text-brand-navy'>{recipe.name}</h4>
                  <button
                    type='button'
                    onClick={() => toggleSaveRecipe(recipe)}
                    className='text-red-500 hover:text-red-600'
                    aria-label={`Remove ${recipe.name} from saved recipes`}
                  >
                    <Icons.close className='h-4 w-4' strokeWidth={1.5} />
                  </button>
                </div>
                <p className='mt-1 line-clamp-2 text-xs text-muted-foreground'>{recipe.description}</p>
                <Button
                  variant='link'
                  size='sm'
                  onClick={() => {
                    if (!recipes.some((r) => r.id === recipe.id)) {
                      setRecipes([recipe, ...recipes])
                    }
                    setExpandedRecipeId(recipe.id)
                    window.scrollTo({ top: 400, behavior: 'smooth' })
                  }}
                  className='mt-2 h-auto p-0 text-xs text-brand-cornflower'
                >
                  View Details
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
